import { List, ListItem, ListItemTextSkeleton, Text } from "@clipboard-health/ui-components";
import { Stack } from "@mui/material";
import { type ReactElement } from "react";

import { Button } from "../components/Button";
import { RolesEmptyStateCard } from "./EmptyStateCard";
import { RoleListItem } from "./RoleListItem/RoleListItem";
import { type WorkerQualificationWithLicenses } from "./useWorkerQualificationsWithLicenses";

interface RolesPageContentProps {
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  autoApprovedQualifications: Set<string>;
  qualificationsWithLicenses: WorkerQualificationWithLicenses[];
  isLoading: boolean;
  isEmpty: boolean;
  onAddRole: () => void;
  onRoleClick: (qualificationId: string) => void;
}

export function RolesPageContent(props: RolesPageContentProps): ReactElement {
  const {
    scrollContainerRef,
    autoApprovedQualifications,
    qualificationsWithLicenses,
    isLoading,
    isEmpty,
    onAddRole,
    onRoleClick,
  } = props;

  return (
    <Stack
      ref={scrollContainerRef}
      sx={{ flexDirection: "column", padding: 5, flex: 1, gap: 4, overflow: "auto" }}
    >
      {!isEmpty && (
        <Text
          variant="body1"
          sx={(theme) => ({ color: theme.palette.text.secondary, paddingBottom: 1 })}
        >
          You&apos;ll see shifts for the following roles:
        </Text>
      )}

      {isLoading && (
        <List>
          {Array.from({ length: 10 }).map((_, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <ListItem key={`skeleton-${index}`} divider>
              <ListItemTextSkeleton primaryWidth="30%" secondaryWidth="80%" />
            </ListItem>
          ))}
        </List>
      )}

      {isEmpty && (
        <RolesEmptyStateCard
          title="You haven't added any roles yet."
          description="Add new role to start browsing available shifts!"
        >
          <Button
            fullWidth
            size="large"
            variant="contained"
            startIconType="plus"
            onClick={onAddRole}
          >
            Add new role
          </Button>
        </RolesEmptyStateCard>
      )}

      {!isEmpty && (
        <List>
          {qualificationsWithLicenses.map((item) => (
            <RoleListItem
              key={item.qualification.id}
              qualificationLabel={item.qualification.attributes.displayLabel}
              licenses={item.licenses}
              requiresLicense={!autoApprovedQualifications.has(item.qualification.attributes.name)}
              onClick={() => {
                onRoleClick(item.qualification.id);
              }}
            />
          ))}
        </List>
      )}
    </Stack>
  );
}
